import React, { useMemo } from 'react';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { pluralizeToString } from '~/utils/stringUtils';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import BlackoutApplyReviewTable from './BlackoutApplyReviewTable';
import type { SalesforceBlackoutDate } from './types';
import { useBlackoutApplyPlan } from './useBlackoutApplyPlan';

interface Props {
  blackoutDates: Array<SalesforceBlackoutDate>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  onClose: () => void;
  propertyId: string;
  roomRateSelections: Array<RoomRateSelection>;
  roomTypeNames: Array<string>;
}

export default function ImportSalesforceBlackoutDatesModal({
  blackoutDates,
  blackoutDatesByRoomRate,
  onClose,
  propertyId,
  roomRateSelections,
  roomTypeNames,
}: Props) {
  const noDocRanges = useMemo(() => ({}), []);

  const { changedPlans, excludedRoomTypeIds, toggleRoomType, totalToCreate, totalToDelete, handleApply, isApplying } =
    useBlackoutApplyPlan({
      blackoutDates,
      docRangesByRoomType: noDocRanges,
      blackoutDatesByRoomRate,
      propertyId,
      roomRateSelections,
      roomTypeNames,
      onApplied: onClose,
    });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Import Salesforce blackout dates</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <BlackoutApplyReviewTable
          changedPlans={changedPlans}
          excludedRoomTypeIds={excludedRoomTypeIds}
          showScopeColumn
          toggleRoomType={toggleRoomType}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          {(totalToCreate > 0 || totalToDelete > 0) && (
            <Typography variant="body2" color="text.secondary">
              <strong>{pluralizeToString('range', totalToCreate)}</strong> to create,{' '}
              <strong>{pluralizeToString('block', totalToDelete)}</strong> to remove
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={isApplying || totalToCreate + totalToDelete === 0}
          >
            Apply
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
